/**
 * Sprint 47 · Store de autenticación del portal Recepción.
 *
 * Reutiliza la infraestructura de cookies HttpOnly del backend.
 * `POST /api/auth/login-recepcion` bloquea a EVENTOS/MARKETING con 403
 * aunque las credenciales sean correctas.
 */
import { api } from "@/services/api";

const KEY_SESSION = "ct_recepcion_session_v1";
const ROLES_RECEPCION = new Set(["RECEPCION", "GERENCIA", "SISTEMAS"]);

const isBrowser = () =>
  typeof window !== "undefined" && typeof window.localStorage !== "undefined";

const readJson = (key, fb) => {
  if (!isBrowser()) return fb;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fb;
  } catch {
    return fb;
  }
};
const writeJson = (key, v) => {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(v));
  } catch {
    /* ignore */
  }
};
const removeKey = (k) => {
  if (!isBrowser()) return;
  try {
    window.localStorage.removeItem(k);
  } catch {
    /* ignore */
  }
};

export const puedeOperarRecepcion = (rol) =>
  !!rol && ROLES_RECEPCION.has(String(rol).toUpperCase());

export const getReceptionSession = () => {
  const s = readJson(KEY_SESSION, null);
  if (!s || !s.usuario) return null;
  return s;
};

const snapshotDe = (u) => ({
  usuario: u.usuario,
  rol: u.rol,
  nombre: u.nombre,
  puesto: u.puesto || "",
});

/** Confirma la sesión con el servidor (`/auth/me`); nunca sólo con localStorage. */
export const verificarSesionRecepcion = async () => {
  try {
    const { data } = await api.get("/auth/me");
    if (!data?.usuario || !puedeOperarRecepcion(data.rol)) {
      removeKey(KEY_SESSION);
      return { ok: false, tipo: "permiso", status: 403, categoria: null };
    }
    const snap = snapshotDe(data);
    writeJson(KEY_SESSION, snap);
    return { ok: true, sesion: snap };
  } catch (err) {
    const status = err?.response?.status ?? null;
    const categoria = err?.response?.headers?.["x-auth-rechazo"] || null;
    if (status === 401 || status === 403) {
      removeKey(KEY_SESSION);
      return { ok: false, tipo: status === 401 ? "sesion" : "permiso", status, categoria };
    }
    return { ok: false, tipo: status ? "servidor" : "red", status, categoria: null };
  }
};

export const loginRecepcion = async (usuario, password) => {
  const u = String(usuario || "").trim().toLowerCase();
  const p = String(password || "");
  if (!u || !p) return { error: "campos" };
  try {
    const { data } = await api.post("/auth/login-recepcion", {
      usuario: u,
      password: p,
    });
    if (!data || !data.usuario) return { error: "sin-usuario" };
  } catch (err) {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    if (status === 403) {
      return {
        error: "forbidden",
        mensaje:
          typeof detail === "string"
            ? detail
            : "Este usuario no tiene permiso para operar Recepción.",
      };
    }
    if (status === 401) return { error: "credenciales" };
    return { error: "network" };
  }
  const v = await verificarSesionRecepcion();
  if (v.ok) return v.sesion;
  if (v.tipo === "sesion") {
    return { error: "sesion-no-conservada", status: v.status, categoria: v.categoria };
  }
  if (v.tipo === "permiso") return { error: "forbidden" };
  return { error: "network" };
};

export const logoutRecepcion = () => {
  removeKey(KEY_SESSION);
  api.post("/auth/logout").catch(() => {});
};

export const clearReceptionSessionLocal = () => removeKey(KEY_SESSION);
